import React, { useEffect, useMemo, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { Formik, ErrorMessage } from 'formik';
import isEqual from 'lodash/isEqual';
import BasicCheckbox from '../../../../../../components/BasicCheckbox';
import DateSelect from '../../../../../../components/DateSelect';
import HorizontalLoader from '../../../../../../components/HorizontalLoader';
import LoadMask from '../../../../../../components/LoadMask';
import {ANIMATION_TIMEOUT_APPEAR} from '../../../../../../constants/animation';
import useApi from '../../../../../../hooks/useApi';
import AsyncCreatableSelectBox from '../AsyncCreatableSelectBox';
import AsyncSelectBox from '../AsyncSelectBox';
import Controls from '../Controls';
import SelectBox from '../SelectBox';
import TextArea from '../TextArea';
import { EditEducationFormValidationSchema } from './validate';
import {
  processGetFormResponseData,
  processPostFormRequestData,
} from './helpers';

const EditEducationForm = props => {
  const {
    actions: { handleError, proceed, dismiss },
    params,
    endpoint,
  } = props;

  const api = useApi();

  const [state, setState] = useState({
    isRecruiter: params.isRecruiter,
    init: false,
    fields: {
      RoleID: { label: 'Student', value: '4' },
      DegreeID: null,
      CurrentSchoolID: null,
      OfficeLocationID: null,
      Description: '',
      Email: '',
      Present: false,
      Active: true,
      Approved: { value: 1, label: 'Yes' },
      AddNewsfeedItem: false,
      SendEmail: false,
      SendDeclineEmail: false,
      startDate: null,
      endDate: null,
    },
  });

  useEffect(() => {
    api.get(
      endpoint.getForm,
      { id: params.id, cId: params.cId },
      {
        handleError: handleError,
        handleSuccess: data => {
          const processedData = processGetFormResponseData(data);
          setState(prevState => ({
            ...prevState,
            ...processedData,
            fields: processedData.fields
              ? { ...prevState.fields, ...processedData.fields }
              : { ...prevState.fields },
            options: processedData.options,
            init: true,
          }));
        },
      }
    );
  }, []);

  const [showSendEmail, showSendDeclineEmail, showAddNewsfeed] = useMemo(() => {
    return [
      !params.id ||
        (state.fields.Approved && state.fields.Approved.value !== 1),
      state.fields.Approved && state.fields.Approved.value !== 2,
      !state.fields.Active,
    ];
  }, [state.fields]);

  const handleSubmit = (values, { setSubmitting, setErrors }) => {
    const data = processPostFormRequestData(
      {
        ...values,
        id: params.id || null,
        cId: params.cId || null,
      },
      params.isMasterAccount
    );

    api.post(endpoint.saveForm, data, {
      handleForbidden: handleError,
      handleError: response => {
        setSubmitting(false);
        handleError(response, setErrors);
      },
      handleSuccess: () =>
        proceed(
          data.Present &&
            state.isRecruiter &&
            ((!isEqual(state.fields.CurrentSchoolID, values.CurrentSchoolID) &&
              state.fields.Present) ||
              !state.fields.Present)
        ),
    });
  };

  return state.init ? (
    <CSSTransition
      in={true}
      appear={true}
      timeout={ANIMATION_TIMEOUT_APPEAR}
      classNames="fade"
    >
      <div className="form__inner">
        <Formik
          initialValues={state.fields}
          validationSchema={EditEducationFormValidationSchema}
          onSubmit={handleSubmit}
        >
          {form => (
            <form onSubmit={handleSubmit} className="form__content">
              {form.isSubmitting && <LoadMask />}
              <SelectBox
                name="RoleID"
                labelText="Role"
                value={form.values.RoleID}
                required={true}
                isValid={true}
                isDisabled={true}
                options={state.options.RoleID}
              />

              <SelectBox
                name="DegreeID"
                labelText="Degree"
                value={form.values.DegreeID}
                required={true}
                isValid={
                  (!form.errors.DegreeID && !!form.touched.DegreeID) ||
                  !form.touched.DegreeID
                }
                options={state.options.DegreeID}
                onChange={form.setFieldValue}
                onBlur={form.setFieldTouched}
              />

              <AsyncCreatableSelectBox
                url={props.endpoint.getSchools}
                name="CurrentSchoolID"
                labelText="School"
                value={form.values.CurrentSchoolID}
                required={true}
                isValid={
                  (!form.errors.CurrentSchoolID &&
                    !!form.touched.CurrentSchoolID) ||
                  !form.touched.CurrentSchoolID
                }
                onChange={form.setFieldValue}
                onBlur={form.setFieldTouched}
              />

              <AsyncSelectBox
                url={props.endpoint.getLocations}
                name="OfficeLocationID"
                labelText="School Location"
                value={form.values.OfficeLocationID}
                isValid={
                  (!form.errors.OfficeLocationID &&
                    !!form.touched.OfficeLocationID) ||
                  !form.touched.OfficeLocationID
                }
                required={true}
                onChange={form.setFieldValue}
                onBlur={form.setFieldTouched}
              />

              <TextArea
                name="Description"
                value={form.values.Description}
                labelText="Description"
                placeholder="0 characters of the 2000 character limit"
                isValid={
                  (!form.errors.Description && !!form.touched.Description) ||
                  !form.touched.Description
                }
                required={false}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                rows={10}
                resize={false}
              />

              <div className="form__group form__group--compact">
                <DateSelect
                  required
                  isLast={true}
                  label="Start Date"
                  name="startDate"
                  controlFunc={form.setFieldValue}
                  touchFunc={form.setFieldTouched}
                  values={form.values.startDate}
                  yearOptions={state.options.Date.year}
                  isValid={
                    (!form.errors.startDate && !!form.touched.startDate) ||
                    !form.touched.startDate
                  }
                />

                {(form.errors.StartMonth || form.errors.startDate) && (
                  <div className="form__error">
                    {form.errors.StartMonth || form.errors.startDate}
                  </div>
                )}
              </div>

              {!form.values.Present && (
                <div className="form__group form__group--compact">
                  <DateSelect
                    required
                    isLast={true}
                    label="End Date"
                    name="endDate"
                    controlFunc={form.setFieldValue}
                    touchFunc={form.setFieldTouched}
                    values={form.values.endDate}
                    yearOptions={state.options.Date.year}
                    isValid={
                      (!form.errors.endDate && !!form.touched.endDate) ||
                      !form.touched.endDate
                    }
                  />

                  <ErrorMessage
                    name="endDate"
                    render={message => (
                      <div className="form__error">{message}</div>
                    )}
                  />
                </div>
              )}

              <div className="form__group form__group--compact">
                <div className="form__row">
                  <BasicCheckbox
                    value={form.values.Present}
                    singleRow
                    checked={form.values.Present}
                    title="Present"
                    name="Present"
                    controlFunc={form.handleChange}
                    labelStyles={{
                      fontFamily: 'Noto Sans, sans-serif',
                    }}
                  />
                </div>
              </div>

              {form.values.Present && (
                <>
                  {!params.isMasterAccount &&
                    state.isRecruiter &&
                    ((!isEqual(
                      state.fields.CurrentFirmID,
                      form.values.CurrentFirmID
                    ) &&
                      state.fields.Present) ||
                      !state.fields.Present) && (
                      <div className="form__group form__group--compact">
                        <span className="position-form__warning">
                          After adding the <strong>current position</strong>,{' '}
                          {props.params.cId ? `this ` : `your `} recruiter
                          account will be blocked as long as the administrator
                          does not confirm the position.
                        </span>
                      </div>
                    )}
                </>
              )}

              {params.isMasterAccount && (
                <>
                  {form.values.Present && (
                    <div className="form__group form__group--compact">
                      <div className="form__row">
                        <BasicCheckbox
                          value={form.values.Active}
                          singleRow
                          checked={form.values.Active}
                          title="Active"
                          name="Active"
                          controlFunc={form.handleChange}
                          labelStyles={{
                            fontFamily: 'Noto Sans, sans-serif',
                          }}
                        />
                      </div>
                    </div>
                  )}

                  <SelectBox
                    name="Approved"
                    value={form.values.Approved || [{ value: 1, label: 'Yes' }]}
                    labelText="Approved"
                    isValid={
                      (!form.errors.Approved && !!form.touched.Approved) ||
                      !form.touched.Approved
                    }
                    required={true}
                    options={state.options.Approved}
                    onChange={form.setFieldValue}
                    onBlur={form.setFieldTouched}
                  />
                  <div className="form__group form__group--compact">
                    {showAddNewsfeed &&
                      form.values.Present &&
                      form.values.Active &&
                      form.values.Approved &&
                      form.values.Approved.value === 1 && (
                        <div className="form__row">
                          <BasicCheckbox
                            value={form.values.AddNewsfeedItem}
                            singleRow
                            checked={!!form.values.AddNewsfeedItem}
                            title="Add newsfeed item"
                            name="AddNewsfeedItem"
                            controlFunc={form.handleChange}
                            labelStyles={{
                              fontFamily: 'Noto Sans, sans-serif',
                            }}
                          />
                        </div>
                      )}
                    {showSendDeclineEmail &&
                      form.values.Approved &&
                      form.values.Approved.value === 2 && (
                        <div className="form__row">
                          <BasicCheckbox
                            value={form.values.SendDeclineEmail}
                            singleRow
                            checked={!!form.values.SendDeclineEmail}
                            title="Send decline notification"
                            name="SendDeclineEmail"
                            controlFunc={form.handleChange}
                            labelStyles={{
                              fontFamily: 'Noto Sans, sans-serif',
                            }}
                          />
                        </div>
                      )}
                  </div>
                </>
              )}

              <Controls
                onSubmit={form.handleSubmit}
                onClose={dismiss}
                submitBtnText={params.id ? 'Update Position' : 'Add Position'}
              />
            </form>
          )}
        </Formik>
      </div>
    </CSSTransition>
  ) : (
    <div className="form__inner">
      <div className="horizontal-loader__container">
        <HorizontalLoader />
      </div>
    </div>
  );
};

export default EditEducationForm;
